package org.teddapp.project.server

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import io.ktor.client.HttpClient
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/** Où la session range ce qu'elle doit retrouver au prochain lancement. */
interface ServerStore {
    suspend fun get(key: String): String?
    suspend fun set(key: String, value: String)
    suspend fun delete(key: String)
}

/**
 * La session de cet appareil auprès du serveur teddApp : à quelle adresse, sous quel
 * compte, avec quel rôle.
 *
 * Le jeton d'accès (15 minutes) ne vit qu'en mémoire. Le jeton de rafraîchissement,
 * propre à l'appareil et changé à chaque usage, est gardé dans les préférences locales.
 *
 * Le rôle affiché n'est qu'une indication pour l'interface : le serveur le revérifie à
 * chaque action réservée.
 */
class ServerSession(
    private val http: HttpClient,
    private val store: ServerStore,
    private val deviceName: String,
    private val platform: String,
    /** Adresse imposée tant qu'aucune n'a été enregistrée : la production, ou celle du domaine sur le web. */
    private val defaultUrl: String = "",
    private val now: () -> Long,
) {
    var url by mutableStateOf(defaultUrl)
        private set
    var email by mutableStateOf("")
        private set
    var role by mutableStateOf(ServerRole.USER)
        private set
    /** Identifiant du compte sur le serveur. */
    var userId by mutableStateOf("")
        private set
    var connected by mutableStateOf(false)
        private set
    /** Vrai une fois les préférences relues : avant, on ne sait pas encore si on est connecté. */
    var restored by mutableStateOf(false)
        private set

    val isSupra: Boolean get() = connected && role == ServerRole.SUPRA

    /** Vrai quand l'adresse est imposée par la plateforme (web) : l'interface ne la demande pas. */
    val urlFixed: Boolean get() = defaultUrl.isNotBlank()

    private var refreshToken: String? = null
    private var accessToken: String? = null
    private var accessExpiresAt = 0L
    private val renewing = Mutex()

    private fun api() = ServerApi(http, url)

    /**
     * Relit la session du dernier lancement. Sans effet si personne n'était connecté.
     *
     * L'adresse imposée ([defaultUrl], la production ou `TEDD_SERVER_URL` en
     * développement) l'emporte sur celle enregistrée : sinon, un poste connecté
     * un jour à la production y resterait même lancé contre un serveur local, sans
     * pouvoir changer d'adresse, le champ étant masqué. Une session prise sur un
     * autre serveur est oubliée : ses jetons ne valent rien ici.
     */
    suspend fun restore() {
        val stored = store.get(KEY_URL)?.takeIf { it.isNotBlank() }
        url = defaultUrl.ifBlank { stored.orEmpty() }
        email = store.get(KEY_EMAIL).orEmpty()
        role = store.get(KEY_ROLE) ?: ServerRole.USER
        userId = store.get(KEY_USER).orEmpty()
        refreshToken = store.get(KEY_REFRESH)
        if (stored != null && defaultUrl.isNotBlank() && normalizeUrl(stored) != normalizeUrl(defaultUrl)) {
            refreshToken = null
            role = ServerRole.USER
            store.delete(KEY_REFRESH)
            store.delete(KEY_ROLE)
        }
        connected = url.isNotBlank() && refreshToken != null
        restored = true
    }

    /** Ouvre une session. Lève [ServerException] si le serveur refuse ou ne répond pas. */
    suspend fun signIn(serverUrl: String, email: String, password: String) {
        val address = normalizeUrl(serverUrl)
        val mail = email.trim().lowercase()
        val tokens = ServerApi(http, address).login(mail, password, deviceId(), deviceName, platform)
        url = address
        this.email = mail
        store.set(KEY_URL, address)
        store.set(KEY_EMAIL, mail)
        adopt(tokens)
        connected = true
    }

    /**
     * Crée le compte sur le serveur et ouvre sa session. Lève [ServerException]
     * (`signup_closed` quand seul l'administrateur crée les comptes, `email_taken`).
     */
    suspend fun signUp(serverUrl: String, email: String, password: String) {
        val address = normalizeUrl(serverUrl)
        val mail = email.trim().lowercase()
        val tokens = ServerApi(http, address).signup(mail, password, deviceId(), deviceName, platform)
        url = address
        this.email = mail
        store.set(KEY_URL, address)
        store.set(KEY_EMAIL, mail)
        adopt(tokens)
        connected = true
    }

    /** Ferme la session, ici et sur le serveur si on peut le joindre. */
    suspend fun signOut() {
        val access = accessToken
        if (connected && access != null && now() < accessExpiresAt) runCatching { api().logout(access) }
        forget()
    }

    /**
     * Exécute un appel authentifié. Le jeton d'accès est renouvelé s'il a expiré, et une
     * fois de plus si le serveur le refuse. Une session révoquée déconnecte l'appareil.
     */
    suspend fun <T> authed(block: suspend (api: ServerApi, access: String) -> T): T {
        if (!connected) throw ServerException(401, "not_connected", "Connecte-toi d'abord au serveur.")
        val api = api()
        return try {
            block(api, freshAccess(force = false))
        } catch (e: ServerException) {
            if (e.status == 401 && e.code == "invalid_token") block(api, freshAccess(force = true)) else throw e
        }
    }

    /** Redemande le rôle au serveur : il a pu changer depuis la connexion. */
    suspend fun refreshRole() {
        val me = authed { api, access -> api.me(access) }
        setRole(me.role)
    }

    /** Un jeton d'accès valable, renouvelé s'il le faut : pour la sonnette, qui s'authentifie elle-même. */
    suspend fun accessToken(): String = freshAccess(force = false)

    private suspend fun freshAccess(force: Boolean): String = renewing.withLock {
        val current = accessToken
        // Trente secondes de marge : un jeton qui expire pendant la requête ne sert à rien.
        if (!force && current != null && now() < accessExpiresAt - 30_000L) return@withLock current
        val refresh = refreshToken ?: throw ServerException(401, "not_connected", "Connecte-toi d'abord au serveur.")
        val tokens = try {
            api().refresh(refresh)
        } catch (e: ServerException) {
            // Le serveur ne reconnaît plus cette session : inutile de garder ses restes.
            if (e.status == 401) forget()
            throw e
        }
        adopt(tokens)
        tokens.access_token
    }

    private suspend fun adopt(tokens: ServerTokens) {
        accessToken = tokens.access_token
        accessExpiresAt = now() + tokens.expires_in * 1000L
        refreshToken = tokens.refresh_token
        store.set(KEY_REFRESH, tokens.refresh_token)
        userId = tokens.user_id
        store.set(KEY_USER, tokens.user_id)
        setRole(tokens.role)
    }

    private suspend fun setRole(value: String) {
        role = value
        store.set(KEY_ROLE, value)
    }

    private suspend fun forget() {
        accessToken = null
        accessExpiresAt = 0L
        refreshToken = null
        connected = false
        role = ServerRole.USER
        store.delete(KEY_REFRESH)
        store.delete(KEY_ROLE)
    }

    /** L'identifiant que l'appareil se donne une fois pour toutes. */
    @OptIn(ExperimentalUuidApi::class)
    private suspend fun deviceId(): String =
        store.get(KEY_DEVICE) ?: Uuid.random().toString().also { store.set(KEY_DEVICE, it) }

    companion object {
        /** Le serveur teddApp. Les applications s'y connectent d'office ; `TEDD_SERVER_URL` le remplace en développement. */
        const val PRODUCTION_URL = "https://api.teddfullstack.dev"

        const val KEY_URL = "server.url"
        const val KEY_EMAIL = "server.email"
        const val KEY_ROLE = "server.role"
        const val KEY_REFRESH = "server.refresh_token"
        const val KEY_DEVICE = "server.device_id"
        const val KEY_USER = "server.user_id"

        /** « tedd.exemple.org/ » devient « https://tedd.exemple.org ». */
        fun normalizeUrl(raw: String): String {
            val t = raw.trim().trimEnd('/')
            return if (t.startsWith("http://") || t.startsWith("https://")) t else "https://$t"
        }
    }
}

/** La session serveur, quand la plateforme en fournit une. Absente : rien de ce qui en dépend ne s'affiche. */
val LocalServerSession = staticCompositionLocalOf<ServerSession?> { null }

/** Range la session dans les préférences locales de l'appareil (non synchronisées). */
class PreferencesServerStore(private val prefs: org.teddapp.project.db.repo.PreferencesRepository) : ServerStore {
    override suspend fun get(key: String): String? = prefs.get(key)
    override suspend fun set(key: String, value: String) = prefs.set(key, value)
    override suspend fun delete(key: String) = prefs.delete(key)
}
